import { FreeText } from "assets/icons";
import { Close, Delete, Move, MoveWithOrangeColor, Pencil, Reset } from "assets/icons/Common";

import "./styles.css";
import classNames from "classnames";
import * as fabric from "fabric";
import { FabricJSCanvas, useFabricJSEditor } from "fabricjs-react";
import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown, DropdownButton } from "react-bootstrap";
import { FaRegCircle } from "react-icons/fa";
import { FaSlash } from "react-icons/fa";
import { GiArrowCursor } from "react-icons/gi";
import styled from "styled-components";

import { MoveIconBtn } from "../FreeText/FreeText.styles";

export const CanvasScreen: React.FC = ({
  state,
  pixelPosition,
  settings,
  setState,
  onClickPicker,
}: any) => {
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [color, setColor] = useState<string>("#35363a");
  const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false); // Track drawing mode
  const [selectedObject, setSelectedObject] = useState<fabric.Object | null>(null); // Track selected object (changed to null)
  const [lineStyle, setLineStyle] = useState<string>("solid"); // Line style
  const [lineSize, setLineSize] = useState<number>(5); // Line size
  const [brushSize, setBrushSize] = useState<number>(5); // Brush size state
  const [isDrawMode, setIsDrawMode] = useState(true);
  const history: fabric.Object[] = [];

  const [isDragged, setIsDragged] = useState<boolean>(true); // Track drawing mode

  useEffect(() => {
    setState({ canvas: settings?.canvas });
  }, [settings]);

  useEffect(() => {
    setState({ ...state });
  }, [isDragged]);

  const { editor, onReady } = useFabricJSEditor();
  const updateColorForFreeDraw = () => {
    if (editor) {
      const canvas = editor.canvas;
      const brush = new fabric.PencilBrush(canvas);
      brush.color = color;
      const ratio = window.devicePixelRatio || 1;
      brush.width = brushSize * ratio;
      canvas.freeDrawingBrush = brush;
    }
  };

  const updateSelectedObjectColor = (newColor: string) => {
    if (editor) {
      const canvas = editor.canvas;
      const activeObject = canvas.getActiveObject();

      if (activeObject) {
        if (activeObject.type === "textbox") {
          (activeObject as fabric.Textbox).set("fill", newColor);
        } else if (activeObject.type === "circle" || activeObject.type === "rect") {
          (activeObject as fabric.Object).set("fill", newColor);
          (activeObject as fabric.Object).set("stroke", newColor);
        } else if (activeObject.type === "path" || activeObject.type === "line") {
          (activeObject as fabric.Path).set("stroke", newColor);
        }
        canvas.renderAll();
      }
    }
  };

  useEffect(() => {
    updateSelectedObjectColor(color);
    updateColorForFreeDraw();
  }, [color]);

  useEffect(() => {
    if (editor) {
      const canvas = editor.canvas;

      // Update color picker to match selected object's color
      canvas.on("selection:created", () => {
        const activeObject = canvas.getActiveObject();
        setSelectedObject(activeObject || null);
        if (activeObject) {
          if (activeObject.type === "textbox") {
            setColor((activeObject as fabric.Textbox).fill as string);
          } else if (activeObject.type === "circle" || activeObject.type === "rect") {
            setColor((activeObject as fabric.Object).fill as string);
          } else if (activeObject.type === "path" || activeObject.type === "line") {
            setColor((activeObject as fabric.Path).stroke as string);
          }
        }
      });

      canvas.on("selection:updated", () => {
        const activeObject = canvas.getActiveObject();
        setSelectedObject(activeObject || null);
        if (activeObject) {
          if (activeObject.type === "textbox") {
            setColor((activeObject as fabric.Textbox).fill as string);
          } else if (activeObject.type === "circle" || activeObject.type === "rect") {
            setColor((activeObject as fabric.Object).fill as string);
          } else if (activeObject.type === "path" || activeObject.type === "line") {
            setColor((activeObject as fabric.Path).stroke as string);
          }
        }
      });

      // Deselect and reset color picker
      canvas.on("selection:cleared", () => {
        setSelectedObject(null);
        setColor("#35363a"); // Reset to default color
      });
    }
  }, [editor]);

  const onAddCircle = () => {
    if (editor) {
      const canvas = editor.canvas;
      canvas.isDrawingMode = false;
      const circle = new fabric.Circle({
        radius: 50,
        fill: color,
        stroke: color,
        strokeWidth: 2,
        left: 100,
        top: 100,
      });
      canvas.add(circle);
      canvas.renderAll();
    }
    setIsAdded(true);
  };

  const onAddRectangle = () => {
    if (editor) {
      const canvas = editor.canvas;
      canvas.isDrawingMode = false;
      const rectangle = new fabric.Rect({
        width: 100,
        height: 60,
        fill: color,
        stroke: color,
        strokeWidth: 2,
        left: 150,
        top: 150,
      });
      canvas.add(rectangle);
      canvas.renderAll();
    }
    setIsAdded(true);
  };

  const toggleDraw = () => {
    setIsDrawingMode(true);

    if (editor) {
      const canvas = editor.canvas;
      const ratio = window.devicePixelRatio || 1;
      canvas.isDrawingMode = true;
      setIsDrawMode(true);
      const brush = new fabric.PencilBrush(canvas);
      brush.color = color;
      brush.width = brushSize * ratio;
      canvas.freeDrawingBrush = brush;
    }
  };

  const selectMode = () => {
    setIsDrawingMode(false);

    if (editor) {
      const canvas = editor.canvas;
      const ratio = window.devicePixelRatio || 1;
      canvas.isDrawingMode = false;
      setIsDrawMode(false);
    }
  };

  const addText = () => {
    if (editor) {
      const canvas = editor.canvas;
      canvas.isDrawingMode = false;
      const ratio = window.devicePixelRatio || 1;

      const textbox = new fabric.Textbox("Enter text here", {
        left: 100 / ratio,
        top: 100 / ratio,
        fill: color,
        fontSize: 20 * ratio,
        width: 200 / ratio,
      });
      canvas.add(textbox);
      canvas.getObjects().forEach((obj) => obj.setCoords());
      canvas.renderAll();
    }
  };

  const undo = () => {
    if (editor && editor.canvas._objects.length > 0) {
      history.push(editor.canvas._objects.pop()!);
      editor.canvas.renderAll();
    }
  };

  const redo = () => {
    if (editor && history.length > 0) {
      editor.canvas.add(history.pop()!);
      editor.canvas.renderAll();
    }
  };

  const clear = () => {
    if (editor) {
      editor.canvas.clear();
      history.length = 0;
    }
  };

  const onAddLine = () => {
    setIsDrawMode(false);
    if (editor) {
      const canvas = editor.canvas;
      canvas.isDrawingMode = false;

      const ratio = window.devicePixelRatio || 1;

      const line = new fabric.Line([50, 50, 200, 200], {
        left: 100,
        top: 100,
        stroke: color,
        strokeWidth: lineSize * ratio,
        selectable: true,
        hasControls: true,
        strokeDashArray: lineStyle === "dashed" ? [10, 5] : lineStyle === "dotted" ? [2, 2] : [],
      });

      canvas.add(line);
      canvas.renderAll();
    }
  };
  const handleLineStyleChange = (style: string | null) => {
    setIsAdded(true);
    if (style) {
      setLineStyle(style);
      if (selectedObject && selectedObject.type === "line") {
        selectedObject.set({
          strokeDashArray: style === "dashed" ? [10, 5] : style === "dotted" ? [2, 2] : [], // Update line dash pattern
        });
        editor?.canvas.renderAll();
      }
    }
  };

  const handleLineSizeChange = (size: number, type: string) => {
    setIsAdded(true);
    if (type == "line-mode") {
      setLineSize(size);
      if (selectedObject && selectedObject.type === "line") {
        selectedObject.set({ strokeWidth: size });
        editor?.canvas.renderAll();
      }
    }
    if (type == "drawing-mode") {
      setBrushSize(size);
    }
  };

  const deleteSelectedObject = () => {
    if (editor && selectedObject) {
      const canvas = editor.canvas;
      canvas.remove(selectedObject);
      setSelectedObject(null);
      canvas.renderAll();
    }
  };

  useEffect(() => {
    const adjustCanvas = () => {
      if (editor) {
        const canvas = editor.canvas;
        if (canvas.isDrawingMode) {
          const brush = canvas.freeDrawingBrush as fabric.PencilBrush;
          const ratio = window.devicePixelRatio || 1;
          brush.width = brushSize * ratio;
        }

        const ratio = window.devicePixelRatio || 1;
        canvas.setWidth(pixelPosition.width * ratio);
        canvas.setHeight(pixelPosition.height * ratio);

        canvas.getObjects().forEach((obj) => obj.setCoords());
        canvas.renderAll();
      }
    };

    adjustCanvas();
    window.addEventListener("resize", adjustCanvas);
    return () => window.removeEventListener("resize", adjustCanvas);
  }, [editor, pixelPosition, brushSize]);

  useEffect(() => {
    if (editor) {
      const canvas = editor.canvas;
      const ratio = window.devicePixelRatio || 1;
      const { width, height } = canvas.getElement().getBoundingClientRect();

      canvas.setWidth(width);
      canvas.setHeight(height);
      canvas.getContext().scale(ratio, ratio);
      canvas.setZoom(1 / ratio);

      canvas.getObjects().forEach((obj) => obj.setCoords());
      canvas.renderAll();
    }
  }, [editor, isAdded]);

  const [activeButton, setActiveButton] = useState<string | null>(null);

  const handleButtonClick = (buttonName: string, callback: () => void) => {
    setActiveButton(buttonName);
    callback();
  };

  const toggleDragged = () => {
    setIsDragged(!isDragged);
  };

  return (
    <>
      <OuterContainer width={pixelPosition.width} height={pixelPosition.height}>
        <div
          className="header"
          // onClick={toggleDragged}
          // onTouchStart={toggleDragged}
        >
          <DraggableWrapper
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "970px",
            }}
          >
            <DraggableButton
              className={!isDragged ? "active" : ""}
              onClick={() => handleButtonClick("move", toggleDragged)}
            >
              {<MoveWithOrangeColor />}
            </DraggableButton>
            <FreeDrawOptionButton
              className={activeButton === "draw" ? "active" : ""}
              onClick={() => handleButtonClick("draw", toggleDraw)}
            >
              {<Pencil />}
            </FreeDrawOptionButton>
            <FreeDrawOptionButton
              className={activeButton === "select" ? "active" : ""}
              onClick={() => handleButtonClick("select", selectMode)}
              // disabled={!isDrawMode}
            >
              {<GiArrowCursor />}
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={() => handleButtonClick("select", onAddCircle)}>
              <FaRegCircle />
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={() => handleButtonClick("select", onAddRectangle)}>
              <RectangleIcon />
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={() => handleButtonClick("select", addText)}>
              <FreeText />
            </FreeDrawOptionButton>
            <ColorPaletteIcon>
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
            </ColorPaletteIcon>
            <FreeDrawOptionButton onClick={clear}>
              <Close />
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={undo}>
              <Reset />
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={redo}>
              {" "}
              <Reset style={{ transform: "rotateY(180deg)" }} />
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={deleteSelectedObject} disabled={!selectedObject}>
              <Delete />
            </FreeDrawOptionButton>
            <FreeDrawOptionButton onClick={() => handleButtonClick("select", onAddLine)}>
              <FaSlash />
            </FreeDrawOptionButton>{" "}
            <SelectComponent
              id="dropdown-basic-button"
              title={lineStyle}
              onSelect={(value: any) => handleLineStyleChange(value)}
              variant="danger"
            >
              <Dropdown.Item eventKey="solid">solid</Dropdown.Item>
              <Dropdown.Item eventKey="dashed">dashed</Dropdown.Item>
              <Dropdown.Item eventKey="dotted">dotted</Dropdown.Item>
            </SelectComponent>
            {/* Line Size Selector */}
            <FreeDrawOptionButton>
              <input
                type="range"
                min="1"
                max="10"
                value={isDrawMode ? brushSize : lineSize}
                onChange={(e) =>
                  handleLineSizeChange(
                    parseInt(e.target.value),
                    isDrawMode ? "drawing-mode" : "line-mode",
                  )
                }
              />
            </FreeDrawOptionButton>
            {/* <button>
            <FaEraser />
          </button> */}
          </DraggableWrapper>
        </div>
        <DraggableWrapper>
          <FabricJSCanvas className="sample-canvas" onReady={onReady} />
        </DraggableWrapper>
      </OuterContainer>
      <MoveIconBtn Icon={Move} />
    </>
  );
};

const DraggableWrapper = styled.div.attrs({
  className: classNames("no-drag"),
})``;

// const DrawingContainer = styled.div<{
//   width: number;
//   height: number;
// }>`
//   width: ${({ width }) => width}px;
//   height: ${({ height }) => height}px;
//   position: relative;
//   z-index: 2; /* Ensure this is above the background */
//   overflow: hidden;
// `;

// const DraggableWrapper = styled.div.attrs({
//   className: classNames("no-drag"),
// })``;

const OuterContainer = styled.div<{
  height: number | undefined;
  width: number | undefined;
}>`
  width: ${({ width }) => (width ? `${width}px` : "100%")};
  height: ${({ height }) => (height ? `${height}px` : "100%")};
  padding: 10px;
  gap: 2cqh;
  overflow: auto;
  position: relative;
`;

export const FreeDrawOptionButton = styled(Button)`
  background-color: #fff;
  border-radius: 8px;
  border: 2px solid rgb(255, 143, 113);
  color: #000; /* Default text color */

  &:disabled {
    background-color: #dcdcdc;
    border-color: rgb(255, 143, 113);
    color: #a0a0a0; /* Muted text color for disabled state */
    cursor: not-allowed;
    box-shadow: none; /* No shadow for disabled state */
  }

  &:hover,
  &:not(:disabled):not(.disabled):active,
  &:focus {
    background-color: #ffe4e4 !important;
    border-color: rgb(255, 143, 113) !important;
    box-shadow: 0 0 0 0.2rem rgba(222, 128, 129, 0.5) !important;
    color: #000 !important;
  }

  &.active {
    background-color: #ffe4e4 !important;
    border-color: rgb(255, 143, 113) !important;
    box-shadow: 0 0 0 0.2rem rgba(222, 128, 129, 0.5) !important;
    color: #000 !important;
    transition: all 0.3s ease; /* Smooth transition */
  }

  svg {
    width: 1.5rem;

    path {
      fill: rgb(255, 143, 113); /* Match the border color */
    }
  }
`;
export const DraggableButton = styled(Button)`
  background-color: #fff;
  border-radius: 8px;
  border: 2px solid rgb(255, 143, 113);
  color: #000; /* Default text color */

  &:disabled {
    background-color: #dcdcdc;
    border-color: rgb(255, 143, 113);
    color: #a0a0a0; /* Muted text color for disabled state */
    cursor: not-allowed;
    box-shadow: none; /* No shadow for disabled state */
  }

  &:not(:disabled):not(.disabled):active,
  &:focus {
    background-color: #fff !important;
    box-shadow: none !important;
    border-color: rgb(255, 143, 113) !important;
    color: #000 !important;
  }
  &:hover,
  &.active {
    background-color: #ffe4e4 !important;
    border-color: rgb(255, 143, 113) !important;
    box-shadow: 0 0 0 0.2rem rgba(222, 128, 129, 0.5) !important;
    color: #000 !important;
    transition: all 0.3s ease; /* Smooth transition */
  }

  svg {
    width: 1.5rem;

    path {
      fill: rgb(255, 143, 113); /* Match the border color */
    }
  }
`;

const RectangleIcon = styled.div`
  display: inline-block;
  width: 24px;
  height: 20px;
  border: 2px solid rgb(255, 143, 113);
  vertical-align: middle;
`;

const ColorPaletteIcon = styled.label`
  margin-bottom: 0;
  input {
    width: 50px;
    height: 46px;
    border-radius: 8px;
    border: 2px solid rgb(255, 143, 113);
    background-color: #fff;
  }
`;

const SelectComponent = styled(DropdownButton)`
  .dropdown-toggle {
    background-color: transparent;
    height: 43px;
    &:hover,
    &:not(:disabled):not(.disabled):active,
    &:focus {
      background-color: #ffe4e4;
      color: #212529;
      border-color: rgb(255, 143, 113);
      box-shadow: none;
    }
  }
`;
